import { useEffect, useState } from "react";
import { Skeleton } from "../components/Skeleton.js";

/**
 * Tela Sessões Órfãs (ORPHAN-MV).
 *
 * Lista transactions que o servidor criou automaticamente ao receber
 * MeterValues sem StartTransaction do CP. É comportamento esperado com
 * firmwares fora do spec (ex: telluspower D24030483) — a tela existe pra
 * o time de operação confirmar que estamos capturando essas leituras.
 *
 * stopReason:
 *   NULL              -> ainda aberta (CP conectado)
 *   orphan_disconnect -> fechou porque CP caiu
 *   orphan_timeout    -> parou de reportar por >30min
 */

interface Stats {
  total: number;
  abertas: number;
  fechadas_disconnect: number;
  fechadas_timeout: number;
  cps_afetados: number;
}

interface OrphanRow {
  id: number;
  ocpp_transaction_id: number | null;
  charger_id: number;
  cp_id: string;
  vendor: string | null;
  model: string | null;
  connector_id: number | null;
  start_time: string;
  stop_time: string | null;
  start_meter_wh: number | null;
  stop_meter_wh: number | null;
  stop_reason: string | null;
  mv_count: number;
  last_meter_wh: number | null;
  last_mv_at: string | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDuration(startIso: string, stopIso: string | null): string {
  const start = new Date(startIso).getTime();
  const end = stopIso ? new Date(stopIso).getTime() : Date.now();
  const min = Math.max(0, Math.round((end - start) / 60000));
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m.toString().padStart(2, "0")}min`;
}

function fmtEnergyKwh(startWh: number | null, endWh: number | null, lastMv: number | null): string {
  // Bug pego em review Frontend-A: quando startWh era null caíamos em `?? 0`
  // e o delta virava o VALOR ABSOLUTO do medidor (ex: 12345 kWh) em vez do
  // consumo da sessão. Sem baseline não dá pra calcular energia — sinaliza "—".
  if (startWh === null || startWh === undefined) return "—";
  const e = endWh ?? lastMv;
  if (e === null || e === undefined) return "—";
  const delta = e - startWh;
  if (delta <= 0) return "0 kWh";
  return `${(delta / 1000).toFixed(2)} kWh`;
}

function statusBadge(row: OrphanRow) {
  if (!row.stop_time) {
    return (
      <span className="nx-badge nx-badge--charging" title="Aberta — carregador ainda reportando">
        aberta
      </span>
    );
  }
  if (row.stop_reason === "orphan_timeout") {
    return (
      <span
        className="nx-badge"
        style={{
          background: "color-mix(in srgb, var(--nx-warning-500) 13%, transparent)",
          color: "var(--nx-warning-500)",
        }}
        title="Parou de reportar por >30min"
      >
        timeout
      </span>
    );
  }
  if (row.stop_reason === "orphan_disconnect") {
    return (
      <span
        className="nx-badge"
        style={{
          background: "color-mix(in srgb, var(--nx-neutral-500) 13%, transparent)",
          color: "var(--nx-neutral-500)",
        }}
        title="Carregador desconectou"
      >
        disconnect
      </span>
    );
  }
  return (
    <span className="nx-badge" style={{ background: "var(--nx-mute-bg)", color: "var(--nx-mute)" }}>
      {row.stop_reason ?? "fechada"}
    </span>
  );
}

export function OrphanSessionsPage() {
  const [data, setData] = useState<{ sessions: OrphanRow[]; stats: Stats | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"" | "open" | "closed">("");
  const [cpFilter, setCpFilter] = useState("");

  useEffect(() => {
    // AbortController cancela request pendente ao trocar de filtro/desmontar.
    // Sem isso, mudanças rápidas em `statusFilter` podiam causar race
    // (payload antigo sobrescrever payload novo).
    const controller = new AbortController();

    const load = async () => {
      try {
        const params = new URLSearchParams();
        if (statusFilter) params.set("status", statusFilter);
        const url = `/api/orphan-sessions${params.toString() ? `?${params}` : ""}`;
        const r = await fetch(url, {
          credentials: "include",
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (controller.signal.aborted) return;
        setData(json);
        setError(null);
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(String(e));
      }
    };

    void load();
    // Interval só bate no backend quando aba visível — poupa recursos.
    const t = setInterval(() => {
      if (!document.hidden) void load();
    }, 20000);
    // Catch-up imediato ao voltar de aba oculta.
    const onVis = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      controller.abort();
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [statusFilter]);

  if (error && !data) {
    return (
      <div className="nx-card">
        <div className="nx-error">Erro ao carregar sessões órfãs: {error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="nx-page-head">
          <div className="nx-page-head__meta">
            <span className="nx-page-head__eyebrow">Operação</span>
            <h1>Recargas em Aberto</h1>
            <p className="nx-page-head__sub">Carregando…</p>
          </div>
        </div>
        <div className="nx-kpi-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={`s-${i}`} variant="kpi" />
          ))}
        </div>
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <Skeleton variant="row" count={6} />
        </div>
      </div>
    );
  }

  const s = data.stats;
  const rows = data.sessions.filter((r) => {
    if (!cpFilter.trim()) return true;
    return r.cp_id.toLowerCase().includes(cpFilter.trim().toLowerCase());
  });

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Operação</span>
          <h1>Recargas em Aberto</h1>
          <p className="nx-page-head__sub">
            Sessões sintéticas criadas automaticamente quando o carregador envia MeterValues sem
            StartTransaction — comportamento esperado com firmwares fora do spec.
          </p>
        </div>
      </div>

      <div
        className="nx-kpi-grid"
        role="status"
        aria-live="polite"
        aria-atomic="false"
      >
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Total</div>
          <div className="nx-kpi__value">{s?.total ?? 0}</div>
        </div>
        <div className="nx-kpi nx-kpi--orange">
          <div className="nx-kpi__label">Abertas agora</div>
          <div className="nx-kpi__value">{s?.abertas ?? 0}</div>
          <div className="nx-kpi__hint">Carregador ainda reportando</div>
        </div>
        <div className="nx-kpi nx-kpi--cyan">
          <div className="nx-kpi__label">Timeout</div>
          <div className="nx-kpi__value">{s?.fechadas_timeout ?? 0}</div>
          <div className="nx-kpi__hint">parou de reportar &gt;30min</div>
        </div>
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Carregadores afetados</div>
          <div className="nx-kpi__value">{s?.cps_afetados ?? 0}</div>
        </div>
      </div>

      <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--nx-space-3)",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ fontSize: "var(--nx-text-md)", margin: 0 }}>Sessões</h2>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
            <label htmlFor="os-cp" style={{ fontSize: ".75rem", color: "var(--nx-mute)" }}>
              Carregador:
            </label>
            <input
              id="os-cp"
              type="text"
              value={cpFilter}
              onChange={(e) => setCpFilter(e.target.value)}
              placeholder="ex: D24030483"
              className="nx-input"
              style={{ width: "10rem", fontFamily: "var(--nx-font-mono, monospace)" }}
              autoComplete="off"
            />
            <label htmlFor="os-status" style={{ fontSize: ".75rem", color: "var(--nx-mute)", marginLeft: ".5rem" }}>
              Status:
            </label>
            <select
              id="os-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "" | "open" | "closed")}
              className="nx-input"
              style={{ width: "8rem" }}
            >
              <option value="">Todas</option>
              <option value="open">Abertas</option>
              <option value="closed">Fechadas</option>
            </select>
            {(cpFilter || statusFilter) && (
              <button
                type="button"
                onClick={() => {
                  setCpFilter("");
                  setStatusFilter("");
                }}
                className="nx-btn nx-btn--ghost"
                style={{ fontSize: ".75rem", padding: ".3rem .6rem" }}
              >
                Limpar
              </button>
            )}
          </div>
        </div>
        {rows.length === 0 ? (
          <p style={{ color: "var(--nx-mute)", margin: 0 }}>Nenhuma sessão órfã com esses filtros.</p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th className="id">TX</th>
                  <th>Carregador</th>
                  <th className="id">Conector</th>
                  <th>Início</th>
                  <th>Duração</th>
                  <th className="num">MeterValues</th>
                  <th className="num">Energia</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="id mono">#{r.ocpp_transaction_id ?? r.id}</td>
                    <td>
                      <div className="mono" style={{ fontWeight: 600 }}>
                        {r.cp_id}
                      </div>
                      {(r.vendor || r.model) && (
                        <div style={{ fontSize: ".7rem", color: "var(--nx-mute)" }}>
                          {[r.vendor, r.model].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="id mono">{r.connector_id ?? "—"}</td>
                    <td>
                      <div>{fmtDate(r.start_time)}</div>
                      {r.stop_time && (
                        <div style={{ fontSize: ".7rem", color: "var(--nx-mute)" }}>
                          → {fmtDate(r.stop_time)}
                        </div>
                      )}
                    </td>
                    <td className="mono">{fmtDuration(r.start_time, r.stop_time)}</td>
                    <td className="num mono">
                      {r.mv_count}
                      {r.last_mv_at && (
                        <div style={{ fontSize: ".65rem", color: "var(--nx-mute)" }}>
                          últ. {fmtDate(r.last_mv_at)}
                        </div>
                      )}
                    </td>
                    <td className="num mono">
                      {fmtEnergyKwh(r.start_meter_wh, r.stop_meter_wh, r.last_meter_wh)}
                    </td>
                    <td>{statusBadge(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
